import { Component, Input, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { IPostbackTestModalConfigData } from '@app/core/types/postback-api-config/postback-api-config.service.types';
import { urlParameters, urlParametersMockedValues, leadsStatuses, leadsStatusesTranslated } from '@app/core/helpers/constants';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { GetUserBasicDataService } from '@app/core/services/get-user-basic-data/get-user-basic-data.service';
import { SelectContainerChosenValue } from '@app/shared/components/select-container/select-container.component';
import { HttpErrorResponse } from '@angular/common/http';
import { PostbackApiConfigService } from '@app/core/services/postback-api-config/postback-api-config.service';
import { GlobalNotificationsService } from '@app/core/services/global-notifications/global-notifications.service';
import { NotificationTypes } from '@app/core/services/global-notifications/global-notifications.model';

@Component({
  selector: 'app-postback-test-modal',
  templateUrl: './postback-test-modal.component.html',
  styleUrls: ['./postback-test-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PostbackTestModalComponent implements OnInit {
  @Input() postbackTestData?: IPostbackTestModalConfigData;

  public usedParameters: string[] = [];
  public postbackUrlForEdit: string = '';
  public currency: string = '';
  public selectData: {
    title: string;
    data: {
      id: number;
      name: string;
    }[];
  } = {
    title: 'postpack_status_title',
    data: []
  };
  public readonly urlParameters = urlParameters;
  public idOfDefaultValue = 0;

  public paramsList: Record<string, string> = {};
  private postbackStatuses = Object.keys(leadsStatuses);
  private leadStatus = 0;

  private inputErrors: Record<string, boolean> = {};

  constructor(
    public activeModal: NgbActiveModal,
    private getUserBasicDataService: GetUserBasicDataService,
    private cdr: ChangeDetectorRef,
    private postbackService: PostbackApiConfigService,
    private globalNotificationsService: GlobalNotificationsService
  ) {}

  ngOnInit() {
    if (this.postbackTestData?.postback_url) {
      this.setInitialUsedParameters(this.postbackTestData.postback_url);
      this.getUserBasicDataService.shareUserDataSubject.subscribe(res => {
        if (res?.user?.currency) {
          this.currency = res.user.currency;
        }
      });
    }
  }

  public setInputValue(event: { system: string; value: string; hasErrors: boolean }) {
    this.paramsList[event.system] = event.value;
    this.inputErrors[event.system] = event.hasErrors;

    if (this.postbackUrlForEdit) {
      const url = this.postbackUrlForEdit;
      const paramName = event.system;
      const paramValue = encodeURIComponent(event.value);

      const bracketRegex = new RegExp(`\\[${paramName}\\]`, 'g');
      const urlParamRegex = new RegExp(`([?&])${paramName}=.*?(&|$)`, 'i');
      const separator = url.indexOf('?') !== -1 ? '&' : '?';

      if (url.includes(`[${paramName}]`)) {
        this.postbackUrlForEdit = url.replace(bracketRegex, paramValue);
      } else if (url.match(urlParamRegex)) {
        this.postbackUrlForEdit = url.replace(urlParamRegex, `$1${paramName}=${paramValue}$2`);
      } else {
        this.postbackUrlForEdit = `${url}${separator}${paramName}=${paramValue}`;
      }

      this.cdr.detectChanges();
    }
  }

  public setSelectedValue(value: SelectContainerChosenValue): void {
    this.leadStatus = value.choosenValue.id;
    this.idOfDefaultValue = this.selectData.data.findIndex(status => status.id === this.leadStatus);
    this.setInputValue({ system: 'status', value: this.leadStatus.toString(), hasErrors: false });
  }

  public setDefaultValues(): void {
    Object.entries(this.paramsList).forEach(([key, value]) => {
      if (!value) {
        this.paramsList[key] = urlParametersMockedValues[key];
      }
    });
  }

  public sendPostbackTest(): void {
    if (this.hasValidationErrors()) {
      this.showNotification('error_occured', NotificationTypes[1]);
      return;
    }

    this.postbackService.testPostback(this.postbackTestData?.postback_id ?? 0, this.paramsList).subscribe(
      res => {
        this.showNotification(res?.data?.message || 'successfully_tested', NotificationTypes[0]);
      },
      (error: HttpErrorResponse) => {
        this.showNotification(error.error.errors.message, NotificationTypes[1], false);
      }
    );
  }

  public closeModal(): void {
    this.activeModal.close();
  }

  private getDisplayStatusName(status: string | number | undefined): string {
    return leadsStatusesTranslated[status as keyof typeof leadsStatusesTranslated] || '';
  }

  private setInitialUsedParameters = (url: string) => {
    this.postbackUrlForEdit = url;
    const initialParams: string[] = [];
    this.urlParameters.forEach(param => {
      if (url.includes(`[${param}]`)) initialParams.push(param);
    });
    this.usedParameters = initialParams;
    this.usedParameters.forEach(param => {
      this.paramsList[param] = '';
    });

    if (this.usedParameters.includes('status')) {
      // this.leadStatus = this.postbackTestData?.lead_status ?? 0;
      if (this.postbackTestData?.lead_status && this.postbackTestData.lead_status >= 0) {
        this.leadStatus = this.postbackTestData.lead_status;
      } else {
        this.leadStatus = 0;
      }
      this.postbackStatuses.slice(0, -1).forEach(status => {
        this.selectData.data.push({
          name: this.getDisplayStatusName(status as keyof typeof leadsStatuses),
          id: parseInt(status, 10)
        });
      });
      this.idOfDefaultValue = this.selectData.data.findIndex(status => status.id === this.leadStatus);

      Promise.resolve().then(() => {
        this.setInputValue({
          system: 'status',
          value: this.leadStatus.toString(),
          hasErrors: false
        });
        this.cdr.detectChanges();
      });
    }
    this.cdr.detectChanges();
  };

  private showNotification(message: string, type: (typeof NotificationTypes)[number], translate: boolean = true): void {
    this.globalNotificationsService.show({
      message: translate && !message.includes('view_translations.') ? `view_translations.${message}` : message,
      type,
      translate,
      isPersistent: false,
      id: ''
    });
  }

  private hasValidationErrors(): boolean {
    const hasErrors = Object.values(this.inputErrors).some(hasError => hasError === true);

    const hasEmptyRequiredFields = this.usedParameters.some(param => !this.paramsList[param] || this.paramsList[param].trim() === '');

    return hasErrors || hasEmptyRequiredFields;
  }
}
